import { useEffect, useState, type ReactNode } from 'react';
import { SITE_PAGE_REVEAL_DURATION, type SitePageRevealDuration } from '../constants/siteMotion';

const DURATION_CLASS: Record<'500' | '700', string> = {
  '500': 'duration-500',
  '700': 'duration-700',
};

interface SiteFadeInProps {
  visible: boolean;
  children: ReactNode;
  duration?: SitePageRevealDuration;
  /** Matches content-list-panel-main vertical rhythm (gap-10). */
  stack?: boolean;
}

/** Opacity crossfade — content stays invisible until `visible` is true. */
function SiteFadeIn({
  visible,
  children,
  duration = SITE_PAGE_REVEAL_DURATION,
  stack = false,
}: SiteFadeInProps) {
  return (
    <div
      className={`transition-opacity ease-in-out ${DURATION_CLASS[duration]} ${
        stack ? 'flex flex-col gap-10' : ''
      } ${visible ? 'opacity-100' : 'opacity-0'}`}
    >
      {children}
    </div>
  );
}

interface SiteFadeInMountProps {
  children: ReactNode;
  duration?: SitePageRevealDuration;
  stack?: boolean;
}

/** Mount hidden, then fade in on the next frame (smooth entry). */
export function SiteFadeInMount({
  children,
  duration = SITE_PAGE_REVEAL_DURATION,
  stack = false,
}: SiteFadeInMountProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <SiteFadeIn visible={visible} duration={duration} stack={stack}>
      {children}
    </SiteFadeIn>
  );
}

interface SitePageRevealProps {
  ready: boolean;
  children: ReactNode;
  duration?: SitePageRevealDuration;
}

/**
 * SSOT page reveal: hidden while waiting, then one coordinated 500ms fade-in.
 * Wrap PageGrid row content in the outer col-span-full shell.
 */
export function SitePageReveal({
  ready,
  children,
  duration = SITE_PAGE_REVEAL_DURATION,
}: SitePageRevealProps) {
  if (!ready) {
    return null;
  }

  return (
    <div className="col-span-full">
      <SitePageRevealContent duration={duration}>
        {children}
      </SitePageRevealContent>
    </div>
  );
}

function SitePageRevealContent({
  children,
  duration = SITE_PAGE_REVEAL_DURATION,
}: Pick<SitePageRevealProps, 'children' | 'duration'>) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <SiteFadeIn visible={visible} duration={duration}>
      {children}
    </SiteFadeIn>
  );
}

/** PageGrid-safe wrapper: spans full grid width before fading in content. */
interface SiteGridFadeInProps {
  children: ReactNode;
  duration?: SitePageRevealDuration;
  stack?: boolean;
}

export function SiteGridFadeIn({
  children,
  duration = SITE_PAGE_REVEAL_DURATION,
  stack = false,
}: SiteGridFadeInProps) {
  return (
    <div className="col-span-full">
      <SiteFadeInMount duration={duration} stack={stack}>
        {children}
      </SiteFadeInMount>
    </div>
  );
}
